import { useEffect, useMemo, useState } from "react";
import { getMintSpaceAnalytics } from "../api/analytics";
import CreatorAnalytics from "../components/campaign/CreatorAnalytics";
import AppHeader from "../components/ds/AppHeader";
import Badge from "../components/ds/Badge";
import ProgressBar from "../components/ds/ProgressBar";
import StatCell from "../components/ds/StatCell";
import { STATUS_TONES } from "../components/discover/campaignDisplay";
import { useTranslation } from "../context/TranslationContext";

const SELECTED_TAB =
  "bg-app-creator-rewards-deep text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--app-creator-rewards-base)]";

const compact = (value) => (value >= 1000 ? `${Math.round(value / 100) / 10}K` : String(Math.round(value)));

/** Figma "Performance chart": one bar per campaign, the tallest in the app's colour. */
const BarChart = ({ rows, field }) => {
  const max = Math.max(...rows.map((row) => row[field] || 0), 1);
  return (
    <div className="flex h-[200px] gap-space-12">
      <div className="flex w-[32px] shrink-0 flex-col justify-between text-caption-1 text-label-tertiary">
        {[1, 0.75, 0.5, 0.25, 0].map((step) => (
          <span key={step}>{compact(max * step)}</span>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 items-end gap-[6px] border-b border-l border-separator-default">
        {rows.map((row, index) => {
          const value = row[field] || 0;
          return (
            <div
              key={`${row.campaignName}-${index}`}
              title={`${row.campaignName}: ${value.toLocaleString()}`}
              className={`min-w-0 max-w-[64px] flex-1 rounded-t-[4px] ${
                value === max ? "bg-app-creator-rewards-base" : "bg-app-creator-rewards-deep"
              }`}
              style={{ height: `${Math.max((value / max) * 100, 4)}%` }}
            />
          );
        })}
      </div>
    </div>
  );
};

const Card = ({ title, hint, end, children, className = "" }) => (
  <section className={`flex min-w-0 flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 ${className}`}>
    <div className="flex flex-wrap items-center justify-between gap-space-8">
      <div className="flex min-w-0 flex-col gap-space-4">
        <h2 className="text-headline text-label-primary">{title}</h2>
        {hint && <p className="text-footnote text-label-secondary">{hint}</p>}
      </div>
      {end}
    </div>
    {children}
  </section>
);

const MintSpaceAnalyticsPage = ({ setToast, mintSpaceId, onFindCampaign }) => {
  const { t } = useTranslation();
  const [isStaff, setIsStaff] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [chart, setChart] = useState("views");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    views: 0,
    payouts: 0,
    submissions: { total: 0, approved: 0, pending: 0, rejected: 0 },
    activeCampaigns: 0,
  });
  const [campaignBreakdown, setCampaignBreakdown] = useState([]);

  useEffect(() => {
    if (!mintSpaceId) return;
    setLoading(true);
    getMintSpaceAnalytics(mintSpaceId)
      .then((res) => {
        if (!res.success || !res.data) return;
        const d = res.data;
        setStats({
          views: d.views || 0,
          payouts: d.payouts || 0,
          submissions: {
            total: d.submissions?.total ?? 0,
            approved: d.submissions?.approved ?? 0,
            pending: d.submissions?.pending ?? 0,
            rejected: d.submissions?.rejected ?? 0,
          },
          activeCampaigns: d.activeCampaigns || 0,
        });
        setCampaignBreakdown(d.campaignBreakdown || []);
        setIsStaff(!!d.isStaff);
        setAdmin(d.admin || null);
      })
      .catch(() => setToast?.(t("common.error")))
      .finally(() => setLoading(false));
  }, [mintSpaceId, setToast, t]);

  const byCampaign = useMemo(
    () => [...campaignBreakdown].sort((a, b) => (b.spent ?? b.earnings ?? 0) - (a.spent ?? a.earnings ?? 0)),
    [campaignBreakdown]
  );

  const formatStatus = (status) => {
    if (!status) return "—";
    const key = `mintSpace.campaignStatus_${status}`;
    const text = t(key);
    return text === key ? status.charAt(0).toUpperCase() + status.slice(1) : text;
  };

  if (loading) {
    return <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("common.loading")}</p>;
  }

  const { submissions } = stats;

  if (!isStaff) {
    return (
      <CreatorAnalytics
        mintSpaceId={mintSpaceId}
        activeCampaigns={stats.activeCampaigns}
        setToast={setToast}
        onFindCampaign={onFindCampaign}
      />
    );
  }

  const totalBudget = admin?.totalBudget ?? 0;
  const statusCounts = admin?.campaignStatusCounts ?? {};
  const used = totalBudget > 0 ? Math.min(stats.payouts / totalBudget, 1) : 0;
  const approvalRate =
    admin?.approvalRatePct ?? (submissions.total > 0 ? Math.round((submissions.approved / submissions.total) * 1000) / 10 : 0);
  const costPerThousand =
    admin?.costPer1KViews ?? (stats.views > 0 ? Math.round((stats.payouts / stats.views) * 100000) / 100 : 0);
  const maxSpend = Math.max(...byCampaign.map((row) => row.spent ?? row.earnings ?? 0), 1);

  return (
    <div className="flex flex-col gap-space-24 font-ds">
      <AppHeader
        app="creator-rewards"
        eyebrow="Creator Rewards"
        title={t("mintSpace.adminAnalyticsTitle")}
        subtitle={t("mintSpace.adminAnalyticsSubtitle")}
      />

      <Card
        title={t("mintSpace.adminBudgetHealth")}
        hint={t("mintSpace.adminBudgetHealthHint")}
        end={
          <p className="text-subheadline-semibold text-app-creator-rewards-soft">
            {t("campaignFlow.budgetUsed", { percent: Math.round(used * 100) })}
          </p>
        }
      >
        <ProgressBar value={used} />
        <div className="mt-space-4 grid grid-cols-2 gap-space-12 min-[900px]:grid-cols-4 min-[900px]:gap-space-24">
          <StatCell label={t("mintSpace.adminTotalBudget")} value={`${totalBudget.toLocaleString()} ₾`} />
          <StatCell label={t("mintSpace.spent")} value={`${stats.payouts.toLocaleString()} ₾`} />
          <StatCell label={t("campaignFlow.remaining")} value={`${(admin?.budgetRemaining ?? 0).toLocaleString()} ₾`} />
          <StatCell label={t("campaignFlow.costPerThousand")} value={costPerThousand > 0 ? `${costPerThousand.toLocaleString()} ₾` : "—"} />
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-space-12 min-[900px]:grid-cols-4 min-[900px]:gap-space-24">
        <StatCell label={t("mintSpace.views")} value={stats.views.toLocaleString()} meta={t("mintSpace.staffViewsBlurb")} />
        <StatCell label={t("mintSpace.adminUniqueCreators")} value={admin?.uniqueCreators ?? 0} meta={t("mintSpace.adminUniqueCreatorsHint")} />
        <StatCell
          label={t("mintSpace.adminApprovalRate")}
          value={submissions.total > 0 ? `${approvalRate}%` : "—"}
          meta={`${submissions.approved}/${submissions.total} ${t("mintSpace.adminApprovedOfTotal")}`}
        />
        <StatCell label={t("mintSpace.activeCampaigns")} value={stats.activeCampaigns} meta={t("mintSpace.runningCampaigns")} />
      </div>

      <div className="grid grid-cols-1 gap-space-24 min-[1000px]:grid-cols-2">
        <Card title={t("mintSpace.adminPipeline")} hint={t("mintSpace.adminPipelineHint")}>
          <div className="grid grid-cols-3 gap-space-12">
            <StatCell label={t("mintSpace.adminPending")} value={submissions.pending} />
            <StatCell label={t("mintSpace.approvedLabel")} value={submissions.approved} />
            <StatCell label={t("mintSpace.adminRejected")} value={submissions.rejected} />
          </div>
        </Card>
        <Card title={t("mintSpace.adminCampaignStatuses")} className="self-start">
          <div className="flex flex-wrap gap-space-8">
            {Object.entries(statusCounts).map(([status, count]) => (
              <Badge key={status} tone={STATUS_TONES[status] || "neutral"}>
                {formatStatus(status)} · {count}
              </Badge>
            ))}
            {Object.keys(statusCounts).length === 0 && (
              <p className="text-subheadline text-label-secondary">{t("mintSpace.adminNoCampaigns")}</p>
            )}
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-space-4">
        <h2 className="text-title-3 text-label-primary">{t("mintSpace.adminCampaignTable")}</h2>
        <p className="text-subheadline text-label-secondary">{t("mintSpace.adminCampaignTableHint")}</p>
      </div>
      <div className="overflow-x-auto rounded-radius-lg bg-bg-raised">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-separator-default bg-bg-inset text-caps-label uppercase text-label-secondary">
              <th className="px-space-16 py-[10px]">{t("mintSpace.adminCampaignColCampaign")}</th>
              <th className="px-space-16 py-[10px]">{t("mintSpace.adminCampaignColStatus")}</th>
              <th className="px-space-16 py-[10px] text-right">{t("mintSpace.adminCampaignColBudget")}</th>
              <th className="px-space-16 py-[10px] text-right">{t("mintSpace.adminCampaignColSpent")}</th>
              <th className="px-space-16 py-[10px] text-right">{t("mintSpace.adminCampaignColViews")}</th>
              <th className="px-space-16 py-[10px] text-right">{t("mintSpace.adminCampaignColSubs")}</th>
            </tr>
          </thead>
          <tbody className="text-subheadline text-label-primary">
            {byCampaign.length === 0 && (
              <tr>
                <td colSpan={6} className="px-space-16 py-space-24 text-center text-label-secondary">
                  {t("mintSpace.adminNoCampaigns")}
                </td>
              </tr>
            )}
            {byCampaign.map((row) => (
              <tr key={String(row.campaignId)} className="border-b border-separator-default last:border-b-0">
                <td className="px-space-16 py-space-12 text-subheadline-semibold">{row.campaignName}</td>
                <td className="px-space-16 py-space-12">{formatStatus(row.status)}</td>
                <td className="px-space-16 py-space-12 text-right tabular-nums">{(row.budget ?? 0).toLocaleString()} ₾</td>
                <td className="px-space-16 py-space-12 text-right tabular-nums">{(row.spent ?? 0).toLocaleString()} ₾</td>
                <td className="px-space-16 py-space-12 text-right tabular-nums">{(row.views ?? 0).toLocaleString()}</td>
                <td className="px-space-16 py-space-12 text-right tabular-nums">
                  {row.submissionsApproved ?? 0}
                  <span className="text-label-secondary"> / {row.submissionsTotal ?? 0}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Card title={t("mintSpace.adminSpendByCampaign")} hint={t("mintSpace.adminSpendByCampaignHint")}>
        {byCampaign.length === 0 && <p className="text-subheadline text-label-secondary">{t("mintSpace.adminNoCampaigns")}</p>}
        {byCampaign.map((row) => {
          const spend = row.spent ?? row.earnings ?? 0;
          return (
            <div key={String(row.campaignId)} className="flex flex-col gap-space-4">
              <div className="flex items-center justify-between gap-space-12 text-footnote">
                <span className="min-w-0 truncate text-label-primary">{row.campaignName}</span>
                <span className="shrink-0 text-label-secondary">{spend.toLocaleString()} ₾</span>
              </div>
              <ProgressBar value={spend / maxSpend} />
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default MintSpaceAnalyticsPage;
